import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import FollowButton from './FollowButton';

const Network = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentUserFollowing, setCurrentUserFollowing] = useState([]);

    useEffect(() => {
        fetchUsers();
        fetchCurrentUserFollowing();
    }, []);

    const fetchCurrentUserFollowing = async () => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                const userDoc = await getDoc(doc(db, 'profiles', currentUser.uid));
                if (userDoc.exists()) {
                    setCurrentUserFollowing(userDoc.data().following || []);
                }
            } catch (error) {
                console.error('Error fetching current user following:', error);
            }
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const currentUser = auth.currentUser;
            const usersRef = collection(db, 'profiles');
            const q = query(usersRef);
            const querySnapshot = await getDocs(q);
            const fetchedUsers = querySnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    isFollowing: currentUserFollowing.includes(doc.id)
                }))
                .filter(user => user.id !== currentUser?.uid); // Exclude current user
            setUsers(fetchedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleFollowChange = async () => {
        await fetchCurrentUserFollowing();
        await fetchUsers();
    };

    const filteredUsers = users.filter(user => 
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <div className="text-red-500 dark:text-red-400 text-xl mb-4">{error}</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Dreamers Network
                </h1>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search dreamers by name, username, or location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-3 pl-10 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
                    />
                    <svg
                        className="absolute left-3 top-3.5 h-5 w-5 text-gray-400 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
            </div>

            <div className="grid gap-4">
                {filteredUsers.map((user) => (
                    <div
                        key={user.id}
                        className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow relative"
                    >
                        <Link
                            to={`/dreamer/${user.username}`}
                            className="flex items-center gap-4 flex-1"
                        >
                            <div className="relative">
                                <img
                                    src={user.profilePic || user.img || "/logo.png"}
                                    alt={user.username}
                                    onError={(e) => { e.target.src = "/logo.png" }}
                                    className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                                />
                                {user.role === 'pro' && (
                                    <span className="absolute bottom-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                                        PRO
                                    </span>
                                )}
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                    {user.username}
                                </h3>
                                {user.name && (
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        {user.name}
                                    </p>
                                )}
                                {user.location && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        📍 {user.location}
                                    </p>
                                )}
                                {user.about && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">
                                        "{user.about.length > 100 ? `${user.about.slice(0, 100)}...` : user.about}"
                                    </p>
                                )}
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    <span>{user.followers?.length || 0} followers</span>
                                    <span>•</span>
                                    <span>{user.following?.length || 0} following</span>
                                </div>
                            </div>
                        </Link>
                        <div className="ml-4">
                            <FollowButton
                                userId={user.id} 
                                username={user.username}
                                targetUserId={user.id}
                                size="small"
                                initialIsFollowing={currentUserFollowing.includes(user.id)}
                                onFollowChange={handleFollowChange}
                            />
                        </div>
                    </div>
                ))}
                {filteredUsers.length === 0 && (
                    <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow">
                        <p className="text-gray-500 dark:text-gray-400">
                            {searchTerm ? 'No dreamers found matching your search' : 'No dreamers found'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Network;
